import React, { useState, useEffect } from "react";
import { ThemeProvider } from "@mui/material/styles";
import {
  Grid,
  Autocomplete,
  Typography,
  Card,
  MenuItem,
} from "@mui/material";
import axios from "axios";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { StyledTextField, StyledDatePicker, theme } from "../StyledComponents";
import { FIELD_SET_IDS } from "../config/defaults/fieldLayoutUtils";
import useSectionFieldLayout from "../config/hooks/useSectionFieldLayout";

const IPDAdmissionReferral = ({
  sectionName,
  activeSection,
  data,
  setData,
  setAppointmentError,
  appointmentError,
  details,
  setDetails,
  fieldLayout: fieldLayoutProp,
}) => {
  const fieldLayout = useSectionFieldLayout(
    fieldLayoutProp,
    FIELD_SET_IDS.ipdAdmission,
  );
  const [authToken] = useState(
    sessionStorage.getItem("accessToken") !== undefined &&
      sessionStorage.getItem("accessToken") !== null
      ? sessionStorage.getItem("accessToken")
      : ""
  );

  const headers = {
    Authorization: `Bearer ${authToken}`,
  };

  const [departments, setDepartments] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [validationErrors, setValidationErrors] = useState(
    () => appointmentError || {}
  );

  const getAllDepartments = () => {
    axios
      .get(
        `${process.env.REACT_APP_BACKEND_ROUTE_URL}get_all_medical_departments/`,
        { headers }
      )
      .then((res) => {
        let updatedList = res.data.department_list.map((dep) => dep.department);
        // For adding outside facility doctors
        updatedList.push("Other/External");
        setDepartments(updatedList);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const getDoctorsByDepartment = (department) => {
    axios
      .post(
        `${process.env.REACT_APP_BACKEND_ROUTE_URL}get_all_doctors_based_on_department/`,
        { department },
        { headers }
      )
      .then((res) => {
        if (res.data.response_list) {
          let updatedDoctors = res.data.response_list.map((doctor) => {
            return {
              doctor_id: doctor.doctor_id,
              name: doctor.name,
            };
          });
          setDoctors(updatedDoctors);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  useEffect(() => {
    getAllDepartments();
  }, []);

  const validateFields = (detail) => {
    const errors = {};

    if (
      fieldLayout.isFieldVisible("admission_details", detail) &&
      fieldLayout.isFieldRequired("admission_details", detail) &&
      !detail.admission_details?.trim()
    ) {
      errors.admission_details = fieldLayout.getValidationMessage(
        "admission_details",
        "Admission Details is a mandatory field.",
      );
    } else if (
      fieldLayout.isFieldVisible("admission_details", detail) &&
      (detail.doctor_details || detail.expected_date) &&
      !detail.admission_details
    ) {
      // Admission details are mandatory if a doctor or date is selected
      errors.admission_details =
        "Admission details are required when doctor or date is selected.";
    }

    if (
      fieldLayout.isFieldVisible("admission_type", detail) &&
      fieldLayout.isFieldRequired("admission_type", detail) &&
      !detail.admission_type
    ) {
      errors.admission_type = fieldLayout.getValidationMessage(
        "admission_type",
        "Admission Type is a mandatory field.",
      );
    } else if (
      fieldLayout.isFieldVisible("admission_type", detail) &&
      detail.admission_details &&
      !detail.admission_type
    ) {
      // Admission type is mandatory if admission details are provided
      errors.admission_type = "Admission type is required.";
    }

    if (
      fieldLayout.isFieldVisible("expected_date", detail) &&
      fieldLayout.isFieldRequired("expected_date", detail) &&
      !detail.expected_date
    ) {
      errors.expected_date = fieldLayout.getValidationMessage(
        "expected_date",
        "Expected Admission Date is a mandatory field.",
      );
    } else if (
      fieldLayout.isFieldVisible("expected_date", detail) &&
      detail.admission_details &&
      !detail.expected_date
    ) {
      // Expected date is mandatory if admission details are provided
      errors.expected_date = "Expected admission date is required.";
    }

    if (
      fieldLayout.isFieldVisible("service_category", detail) &&
      fieldLayout.isFieldRequired("service_category", detail) &&
      !detail.service_category
    ) {
      errors.service_category = fieldLayout.getValidationMessage(
        "service_category",
        "Department is a mandatory field.",
      );
    } else if (
      fieldLayout.isFieldVisible("service_category", detail) &&
      detail.doctor_details &&
      !detail.service_category
    ) {
      // Department is mandatory if a doctor is selected
      errors.service_category =
        "Department selection is required if a doctor is selected.";
    }

    if (
      fieldLayout.isFieldVisible("doctor_details", detail) &&
      fieldLayout.isFieldRequired("doctor_details", detail) &&
      !detail.doctor_details
    ) {
      errors.doctor_details = fieldLayout.getValidationMessage(
        "doctor_details",
        "Doctor is a mandatory field.",
      );
    } else if (
      fieldLayout.isFieldVisible("doctor_details", detail) &&
      detail.service_category &&
      !detail.doctor_details
    ) {
      // Doctor is mandatory if a department is selected
      errors.doctor_details =
        "Doctor selection is required if a department is selected.";
    }

    setValidationErrors(errors);
    if (typeof setAppointmentError === "function") {
      setAppointmentError(errors);
    }
  };

  const handleInputChange = (field, value) => {
    let updatedDetails = { ...(details || {}) };

    if (field === "service_category") {
      if (!value) {
        setDoctors([]);
      } else {
        getDoctorsByDepartment(value);
      }

      updatedDetails = {
        ...updatedDetails,
        service_category: value,
        doctor_details: null,
      };
    } else if (field === "admission_type") {
      updatedDetails = {
        ...updatedDetails,
        admission_type: value,
        expected_date:
          value === "Immediate"
            ? dayjs().format("YYYY-MM-DD")
            : updatedDetails.expected_date,
      };
    } else {
      updatedDetails = {
        ...updatedDetails,
        [field]: value,
      };
    }

    const newData = [{ ...updatedDetails }];
    setDetails(updatedDetails);
    if (typeof setData === "function") {
      setData(newData);
    }
    validateFields(newData[0]);
  };

  if (!activeSection) {
    return null;
  }

  return (
    <ThemeProvider theme={theme}>
      <Card
        sx={{
          borderRadius: "10px",
          boxShadow: "0 4px 4px 0 #C7C7CC inset",
          backgroundColor: "#FFF",
          marginTop: 1,
        }}
      >
        <Grid item xs={12} sm={12} md={12}>
          <Typography
            sx={{ marginLeft: 1 }}
            color="textPrimary"
            style={{ marginTop: "1%" }}
          >
            Admission
          </Typography>
          <Grid
            container
            spacing={2}
            sx={{
              padding: "10px",
            }}
          >
            {fieldLayout.isFieldVisible("admission_details", details) && (
            <Grid item {...fieldLayout.getFieldGrid("admission_details", { xs: 6, sm: 8, md: 8 })}>
              <StyledTextField
                fullWidth
                label={fieldLayout.getFieldLabel("admission_details", "Admission Details", details)}
                value={details?.admission_details || ""}
                onChange={(e) =>
                  handleInputChange("admission_details", e.target.value)
                }
                error={!!validationErrors?.admission_details}
                helperText={validationErrors?.admission_details}
              />
            </Grid>
            )}

            {fieldLayout.isFieldVisible("admission_type", details) && (
            <Grid item {...fieldLayout.getFieldGrid("admission_type", { xs: 6, sm: 4, md: 4 })}>
              <StyledTextField
                select
                fullWidth
                label={fieldLayout.getFieldLabel("admission_type", "Admission Type", details)}
                value={details?.admission_type || ""}
                onChange={(e) =>
                  handleInputChange("admission_type", e.target.value)
                }
                error={!!validationErrors?.admission_type}
                helperText={validationErrors?.admission_type}
              >
                <MenuItem value="Planned" sx={{ fontSize: "12px" }}>
                  Planned
                </MenuItem>
                <MenuItem value="Immediate" sx={{ fontSize: "12px" }}>
                  Immediate
                </MenuItem>
              </StyledTextField>
            </Grid>
            )}

            {fieldLayout.isFieldVisible("expected_date", details) && (
            <Grid item {...fieldLayout.getFieldGrid("expected_date", { xs: 6, sm: 4, md: 4 })}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <StyledDatePicker
                  label={fieldLayout.getFieldLabel("expected_date", "Expected Admission Date", details)}
                  format="DD-MM-YYYY"
                  minDate={dayjs()}
                  value={
                    details?.expected_date
                      ? dayjs(details.expected_date)
                      : null
                  }
                  onChange={(date) => {
                    const formattedDate = date
                      ? dayjs(date).format("YYYY-MM-DD")
                      : null;
                    handleInputChange("expected_date", formattedDate);
                  }}
                  slotProps={{
                    textField: {
                      error: !!validationErrors?.expected_date,
                      helperText: validationErrors?.expected_date,
                    },
                    field: { clearable: true },
                  }}
                />
              </LocalizationProvider>
            </Grid>
            )}

            {fieldLayout.isFieldVisible("service_category", details) && (
            <Grid item {...fieldLayout.getFieldGrid("service_category", { xs: 6, sm: 4 })}>
              <Autocomplete
                options={departments}
                value={details?.service_category ?? null}
                onChange={(event, newValue) =>
                  handleInputChange("service_category", newValue)
                }
                slotProps={{
                  paper: {
                    sx: {
                      "& .MuiAutocomplete-listbox": {
                        fontSize: "12px",
                      },
                    },
                  },
                }}
                renderInput={(params) => (
                  <StyledTextField
                    {...params}
                    label={fieldLayout.getFieldLabel("service_category", "Select Department", details)}
                    fullWidth
                    error={!!validationErrors?.service_category}
                    helperText={validationErrors?.service_category}
                  />
                )}
              />
            </Grid>
            )}

            {fieldLayout.isFieldVisible("doctor_details", details) && (
            <Grid item {...fieldLayout.getFieldGrid("doctor_details", { xs: 6, sm: 4 })}>
              <Autocomplete
                key={
                  details?.service_category === "Other/External"
                    ? "other-category-ipd"
                    : "default-category-ipd"
                }
                options={doctors}
                getOptionLabel={(option) =>
                  typeof option === "string" ? option : option?.name || ""
                }
                freeSolo={details?.service_category === "Other/External"}
                value={details?.doctor_details || null}
                slotProps={{ paper: { sx: { "& .MuiAutocomplete-listbox": { fontSize: "12px" } } } }}
                renderInput={(params) => (
                  <StyledTextField
                    {...params}
                    label={fieldLayout.getFieldLabel(
                      "doctor_details",
                      details?.service_category === "Other/External"
                        ? "Type a Referred Doctor"
                        : "Select Referred Doctor",
                      details,
                    )}
                    variant="outlined"
                    error={!!validationErrors?.doctor_details}
                    helperText={validationErrors?.doctor_details}
                  />
                )}
                onChange={(event, value) => {
                  if (value) {
                    handleInputChange("doctor_details", value);
                  }
                }}
                onInputChange={(event, newInputValue) => {
                  if (details?.service_category === "Other/External") {
                    handleInputChange("doctor_details", {
                      doctor_id: `9999-${newInputValue}`,
                      name: newInputValue,
                    });
                  }
                }}
              />
            </Grid>
            )}
          </Grid>
        </Grid>
      </Card>
    </ThemeProvider>
  );
};

export default IPDAdmissionReferral;

